import { Typography, Box, Paper, Alert, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { SignInButton } from '../components/SignInButton';
import { JoinCard } from '../components/JoinCard';
import { JoinConfirmation } from '../components/JoinConfirmation';
import { TRUST_COPY } from '../constants/trustCopy';
import { useMembership } from '../hooks/useMembership';
import { useEffect, useState, useCallback } from 'react';

interface UserInfo {
  clientPrincipal: {
    userId: string;
    userRoles: string[];
    claims: Array<{ typ: string; val: string }>;
    identityProvider: string;
    userDetails: string;
  } | null;
  displayName?: string;
  email?: string;
}

type JoinState = 'idle' | 'joining' | 'joined' | 'error';

/** Landing page with trust-first messaging for prospective participants. */
export function HomePage() {
  const [userInfo, setUserInfo] = useState<UserInfo | null>({
    clientPrincipal: null,
  });
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joinState, setJoinState] = useState<JoinState>('idle');
  const [joinError, setJoinError] = useState<string | null>(null);

  // Check existing membership status
  const { membership, loading: membershipLoading, refetch: refetchMembership } = useMembership();

  useEffect(() => {
    // Fetch user info from EasyAuth endpoint
    fetch('/.auth/me')
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch user info');
        }
        const contentType = response.headers.get('content-type') ?? '';
        if (!contentType.includes('application/json')) {
          throw new Error('Unexpected response format');
        }
        return response.json();
      })
      .then((data) => {
        const principal = data.clientPrincipal;
        if (principal) {
          // Extract display name and email from claims
          const nameClaim = principal.claims?.find(
            (c: { typ: string; val: string }) =>
              c.typ === 'name' ||
              c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'
          );
          const emailClaim = principal.claims?.find(
            (c: { typ: string; val: string }) =>
              c.typ === 'email' ||
              c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress' ||
              c.typ === 'preferred_username'
          );

          setUserInfo({
            clientPrincipal: principal,
            displayName: nameClaim?.val || principal.userDetails,
            email: emailClaim?.val,
          });
        } else {
          setUserInfo({ clientPrincipal: null });
        }
        setAuthLoading(false);
      })
      .catch((err) => {
        setError(err.message ?? 'Error loading user info');
        setUserInfo({ clientPrincipal: null });
        setAuthLoading(false);
      });
  }, []);

  const handleJoin = useCallback(async () => {
    setJoinState('joining');
    setJoinError(null);

    try {
      const response = await fetch('/api/memberships/join', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Please sign in to join.');
        }
        throw new Error('Failed to join. Please try again.');
      }

      await response.json();
      setJoinState('joined');
      // Refetch membership to update state
      refetchMembership();
    } catch (err) {
      setJoinError(err instanceof Error ? err.message : 'An error occurred');
      setJoinState('error');
    }
  }, [refetchMembership]);

  const handleNotNow = useCallback(() => {
    // For now, just do nothing - user can sign out or navigate away
    // Future: could dismiss the join card temporarily
  }, []);

  const isAuthenticated = userInfo?.clientPrincipal !== null;
  const loading = authLoading || membershipLoading;
  // User just joined this session (show JoinConfirmation initially, then landing will switch)
  const justJoined = joinState === 'joined';

  return (
    <AppLayout header={<AppHeader userName={userInfo?.displayName} />} footer={<AppFooter />}>
      <Box sx={{ py: 4 }}>
        {loading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Loading user info...
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading user info: {error}
          </Alert>
        )}
        {joinError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setJoinError(null)}>
            {joinError}
          </Alert>
        )}

        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 300 }}>
          Collabolatte
        </Typography>

        <Typography variant="body1" sx={{ mb: 2, color: 'text.primary', maxWidth: '60ch' }}>
          A lightweight way to meet colleagues you might not otherwise encounter.
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', maxWidth: '60ch' }}>
          {TRUST_COPY.OPTIONAL_NO_TRACKING}
        </Typography>

        {!isAuthenticated ? (
          // Unauthenticated: show sign-in prompt
          <Paper elevation={0} sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 500 }}>
              How it works
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              If you join, you may be matched with a colleague for an optional conversation. No
              profile needed, no pressure to participate.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              <Link
                component={RouterLink}
                to="/transparency"
                sx={{ textDecoration: 'none', color: 'inherit', borderBottom: '1px solid' }}
              >
                {TRUST_COPY.TRANSPARENCY_LINK}
              </Link>
            </Typography>
            <SignInButton />
          </Paper>
        ) : justJoined ? (
          // Just joined this session: show confirmation (before membership refetch completes)
          <JoinConfirmation userName={userInfo?.displayName} />
        ) : (
          // Authenticated but not joined: show join card
          <JoinCard
            onJoin={handleJoin}
            onNotNow={handleNotNow}
            isJoining={joinState === 'joining'}
          />
        )}
      </Box>
    </AppLayout>
  );
}
